import { useState, useCallback } from "react";
import JSZip from "jszip";
import { ToolContainer, FileDropZone, ProcessingState, DownloadButton, downloadBlob } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { uniqueNames, isZipName, safeEntryPath, uniquePaths, formatBytes, zipCompressionOptions, archiveFileName, splitPath, type ZipLevel } from "./zipUtils";
import { X } from "lucide-react";

const TOOL_INFO_CONTENT = {
  what: `Creates ZIP archives from multiple files, or extracts files from an existing ZIP archive, entirely in your browser.`,
  how: `Upload files to compress into a ZIP, or upload a ZIP file to extract its contents. Download the result.`,
  tip: `Compressing already-compressed file types (like JPGs or MP4s) into a ZIP typically saves very little space, since they're already tightly compressed — ZIP is much more effective on text-heavy or uncompressed files.`,
};

const PAGE = 100;
type Item = { path: string; blob: Blob };

export default function ZipUnzip() {
  const [files, setFiles] = useState<File[]>([]);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<Blob | null>(null);
  const [extracted, setExtracted] = useState<Item[]>([]);
  const [shown, setShown] = useState(PAGE);
  const [error, setError] = useState<string | null>(null);
  const [archiveName, setArchiveName] = useState("");
  const [level, setLevel] = useState<ZipLevel>("best");
  const [resultName, setResultName] = useState("archive.zip");
  const [rezipping, setRezipping] = useState(false);

  const clearOutputs = () => { setResult(null); setExtracted([]); setShown(PAGE); setError(null); };
  const handleFiles = useCallback((newFiles: File[]) => { setFiles(prev => [...prev, ...newFiles]); setResult(null); setExtracted([]); setShown(PAGE); setError(null); }, []);

  const zipFile = files.find(f => isZipName(f.name));
  const hasZip = !!zipFile;
  // A .zip among the files means extraction is what's wanted. Re-zipping it together with
  // other files is offered separately ("Add all to a new ZIP"), so it is a clear choice.
  const createDisabled = hasZip;
  const extractDisabled = !hasZip;
  const otherFiles = hasZip ? files.length - 1 : 0;
  const finalName = archiveFileName(archiveName, files[0]?.name);

  // Nothing is charged here: a use is counted when the ZIP (or an extracted file) is downloaded.
  const buildZip = async (list: File[]) => {
    if (list.length === 0) return;
    setProcessing(true);
    setError(null);
    try {
      const zip = new JSZip();
      // Same-named files get "(2)", "(3)" instead of silently overwriting each other.
      const names = uniqueNames(list.map(f => f.name));
      const renamed = names.filter((n, i) => n !== list[i].name).length;
      for (let i = 0; i < list.length; i++) zip.file(names[i], await list[i].arrayBuffer(), { date: new Date(list[i].lastModified) });
      const blob = await zip.generateAsync({ type: "blob", ...zipCompressionOptions(level) });
      setExtracted([]);
      setResult(blob);
      setResultName(finalName);
      const before = list.reduce((a, f) => a + f.size, 0);
      toast.success(`ZIP created: ${formatBytes(before)} \u2192 ${formatBytes(blob.size)}${renamed ? ` (${renamed} duplicate name${renamed > 1 ? "s" : ""} renamed)` : ""}`);
    } catch { toast.error("Failed to create ZIP"); }
    finally { setProcessing(false); }
  };

  const extractZip = async () => {
    if (!zipFile) { toast.error("No ZIP file found"); return; }
    setProcessing(true);
    setError(null);
    setResult(null);
    try {
      let zip: JSZip;
      try { zip = await JSZip.loadAsync(await zipFile.arrayBuffer()); }
      catch { setExtracted([]); setError("This file isn't a valid ZIP"); return; }
      const entries = Object.entries(zip.files).filter(([, f]) => !f.dir);
      const paths = uniquePaths(entries.map(([n]) => safeEntryPath(n)));
      const items: Item[] = [];
      for (let i = 0; i < entries.length; i++) items.push({ path: paths[i], blob: await entries[i][1].async("blob") });
      setExtracted(items);
      setShown(PAGE);
      toast.success(`Extracted ${items.length} file${items.length === 1 ? "" : "s"}!`);
    } catch { setExtracted([]); setError("This file isn't a valid ZIP"); }
    finally { setProcessing(false); }
  };

  // One ZIP of everything extracted, folders kept. downloadBlob counts the trial use once for the visit.
  const downloadAll = async () => {
    setRezipping(true);
    try {
      const zip = new JSZip();
      for (const it of extracted) zip.file(it.path, it.blob);
      const blob = await zip.generateAsync({ type: "blob", ...zipCompressionOptions("best") });
      const base = zipFile ? zipFile.name.replace(/\.zip$/i, "") : "extracted";
      downloadBlob(blob, `${base}-extracted.zip`);
    } catch { toast.error("Couldn't build the ZIP"); }
    finally { setRezipping(false); }
  };

  const totalExtracted = extracted.reduce((a, f) => a + f.blob.size, 0);

  return (
    <ToolContainer>
      <FileDropZone gateAtDownload accept="*" multiple onFiles={handleFiles} label="Drop files to ZIP or a ZIP to extract" description="Create or extract ZIP archives" />
      {files.length > 0 && (
        <div className="mt-4 space-y-3">
          <ul className="text-sm space-y-1">
            {files.map((f, i) => (
              <li key={f.name + i} className="flex items-center gap-2 border rounded px-2 py-1">
                <span className="flex-1 truncate">{f.name}</span>
                <span className="text-xs text-muted-foreground">{formatBytes(f.size)}</span>
                <button aria-label={`Remove ${f.name}`} onClick={() => { setFiles(a => a.filter((_, k) => k !== i)); clearOutputs(); }}><X className="h-3.5 w-3.5" /></button>
              </li>
            ))}
          </ul>
          {hasZip && otherFiles > 0 && (
            <div className="rounded border border-border bg-muted/40 p-3 text-xs space-y-2">
              <p>
                <strong>{zipFile!.name}</strong> is a ZIP, so "Extract ZIP" unpacks it and ignores the other {otherFiles} file{otherFiles === 1 ? "" : "s"}.
                To pack all {files.length} files (including the ZIP) into one new archive instead, use "Add all to a new ZIP".
              </p>
              <Button size="sm" variant="outline" disabled={processing} onClick={() => buildZip(files)}>Add all to a new ZIP</Button>
            </div>
          )}
          {!hasZip && (
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label htmlFor="zip-archive-name" className="text-xs text-muted-foreground block mb-1">Archive name</label>
                <input
                  id="zip-archive-name"
                  value={archiveName}
                  onChange={e => setArchiveName(e.target.value)}
                  placeholder={finalName.replace(/\.zip$/i, "")}
                  className="h-9 w-56 rounded-md border bg-background px-2 text-sm"
                />
              </div>
              <div>
                <label htmlFor="zip-level" className="text-xs text-muted-foreground block mb-1">Compression</label>
                <select id="zip-level" value={level} onChange={e => setLevel(e.target.value as ZipLevel)} className="h-9 rounded-md border bg-background px-2 text-sm">
                  <option value="store">Store (no compression)</option>
                  <option value="fast">Fast</option>
                  <option value="best">Best (smallest)</option>
                </select>
              </div>
              <span className="text-xs text-muted-foreground pb-2">Saved as {finalName}</span>
            </div>
          )}
          <div className="flex gap-3">
            {processing ? <ProcessingState /> : (
              <>
                <Button onClick={() => buildZip(files)} disabled={createDisabled} title={createDisabled ? "A ZIP file is already selected \u2014 remove it first if you want to create a new archive instead of extracting this one" : undefined}>Create ZIP</Button>
                <Button variant="outline" onClick={extractZip} disabled={extractDisabled} title={extractDisabled ? "No ZIP file selected \u2014 add one to extract, or use Create ZIP to archive the files you've added" : undefined}>Extract ZIP</Button>
                <Button variant="ghost" onClick={() => { setFiles([]); clearOutputs(); }}>Clear</Button>
              </>
            )}
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          {result && <DownloadButton blob={result} fileName={resultName} label="Download ZIP" />}
          {extracted.length > 0 && (
            <div className="space-y-1 pt-3 border-t">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-xs text-muted-foreground">{extracted.length} file{extracted.length === 1 ? "" : "s"}, {formatBytes(totalExtracted)} in total. Folders are kept in the paths below.</p>
                <Button size="sm" onClick={downloadAll} disabled={rezipping}>{rezipping ? "Building ZIP..." : "Download all (ZIP)"}</Button>
              </div>
              {extracted.slice(0, shown).map((f, i) => {
                const { dir, name } = splitPath(f.path);
                return (
                  <div key={i} className="flex items-center justify-between gap-2 p-2 rounded border text-sm">
                    <span className="min-w-0 truncate" title={f.path}>
                      {dir && <span className="text-muted-foreground">{dir}</span>}{name}
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-muted-foreground">{formatBytes(f.blob.size)}</span>
                      <Button size="sm" variant="ghost" aria-label={`Download ${f.path}`} onClick={() => downloadBlob(f.blob, name)}>Download</Button>
                    </span>
                  </div>
                );
              })}
              {extracted.length > shown && (
                <Button size="sm" variant="outline" onClick={() => setShown(n => n + PAGE)}>
                  Show more ({Math.min(PAGE, extracted.length - shown)} of {extracted.length - shown} remaining)
                </Button>
              )}
            </div>
          )}
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
